import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import "./ImageEditor.css";

function isPointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    if (
      polygon[i].y > point.y !== polygon[j].y > point.y &&
      point.x <
        ((polygon[j].x - polygon[i].x) * (point.y - polygon[i].y)) /
          (polygon[j].y - polygon[i].y) +
          polygon[i].x
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function getImageWithDrawings(image, drawings) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!image) {
    return "";
  }

  // Set canvas to image's natural size
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  // Draw the original image
  ctx.drawImage(image, 0, 0);

  // Draw all the black shapes on top
  ctx.fillStyle = "black";
  ctx.strokeStyle = "black";
  ctx.lineWidth = 8;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  drawings.forEach((drawing) => {
    if (drawing.type === "polygon" && drawing.points.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(drawing.points[0].x, drawing.points[0].y);

      for (let i = 1; i < drawing.points.length; i++) {
        ctx.lineTo(drawing.points[i].x, drawing.points[i].y);
      }

      // If it's a closed shape (polygon), fill it
      if (drawing.closed && drawing.points.length >= 3) {
        ctx.closePath();
        ctx.fill();
      } else {
        // Otherwise, just stroke the lines
        ctx.stroke();
      }
    }
  });

  return canvas.toDataURL("image/jpeg");
}

const ImageEditor = (props) => {
  const { setImageEditorRef, imageSrc } = props;
  const [drawings, setDrawings] = useState([]);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);

  // Create a mock editor interface to maintain compatibility with existing code
  const editorInterface = {
    getImageName: () => imageSrc?.navn || "edited-image",
    toDataURL: () => {
      if (imgRef.current) {
        return getImageWithDrawings(imgRef.current, drawings);
      }
      return "";
    },
  };

  useEffect(() => {
    if (setImageEditorRef) {
      setImageEditorRef(editorInterface);
    }
  }, [setImageEditorRef, drawings]);

  // Get coordinates relative to the image's natural size
  const getImageCoordinates = useCallback((clientX, clientY) => {
    if (!imgRef.current || !overlayRef.current) return null;

    const rect = overlayRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Convert to image coordinates
    const scaleX = imgRef.current.naturalWidth / imgRef.current.clientWidth;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.clientHeight;

    return {
      x: x * scaleX,
      y: y * scaleY,
    };
  }, []);

  // Check if a point is close to the first point (for closing polygon)
  const isCloseToFirstPoint = useCallback(
    (point, firstPoint, tolerance = 10) => {
      if (!point || !firstPoint) return false;

      // Convert tolerance to image coordinates
      const scaleX = imgRef.current.naturalWidth / imgRef.current.clientWidth;
      const scaledTolerance = tolerance * scaleX;

      const distance = Math.sqrt(
        Math.pow(point.x - firstPoint.x, 2) +
          Math.pow(point.y - firstPoint.y, 2),
      );

      return distance <= scaledTolerance;
    },
    [],
  );

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      const point = getImageCoordinates(e.clientX, e.clientY);
      if (!point) return;

      if (!currentDrawing) {
        // Start a new polygon
        const newDrawing = {
          type: "polygon",
          points: [point],
          closed: false,
        };
        setCurrentDrawing(newDrawing);
        setIsDrawing(true);
      } else {
        // Add point to current polygon
        const firstPoint = currentDrawing.points[0];

        // Check if clicking close to first point to close the polygon
        if (
          currentDrawing.points.length >= 3 &&
          isCloseToFirstPoint(point, firstPoint)
        ) {
          // Close the polygon
          const closedDrawing = {
            ...currentDrawing,
            closed: true,
          };
          setDrawings((prev) => [...prev, closedDrawing]);
          setCurrentDrawing(null);
          setIsDrawing(false);
        } else {
          // Add new point
          setCurrentDrawing((prev) => ({
            ...prev,
            points: [...prev.points, point],
          }));
        }
      }
    },
    [currentDrawing, getImageCoordinates, isCloseToFirstPoint],
  );

  const handleDoubleClick = useCallback(
    (e) => {
      e.preventDefault();

      // Finish current polygon without closing it
      if (currentDrawing && currentDrawing.points.length >= 2) {
        setDrawings((prev) => [...prev, currentDrawing]);
        setCurrentDrawing(null);
        setIsDrawing(false);
      }
    },
    [currentDrawing],
  );

  const clearDrawings = () => {
    setDrawings([]);
    setCurrentDrawing(null);
    setIsDrawing(false);
  };

  const undoLastDrawing = () => {
    if (currentDrawing) {
      // If currently drawing, remove last point or cancel drawing
      if (currentDrawing.points.length > 1) {
        setCurrentDrawing((prev) => ({
          ...prev,
          points: prev.points.slice(0, -1),
        }));
      } else {
        setCurrentDrawing(null);
        setIsDrawing(false);
      }
    } else {
      // Remove last completed drawing
      setDrawings((prev) => prev.slice(0, -1));
    }
  };

  const cancelCurrentDrawing = () => {
    setCurrentDrawing(null);
    setIsDrawing(false);
  };

  // Convert drawing coordinates back to display coordinates for SVG
  const getDisplayCoordinates = useCallback((point) => {
    if (!imgRef.current) return point;

    const scaleX = imgRef.current.clientWidth / imgRef.current.naturalWidth;
    const scaleY = imgRef.current.clientHeight / imgRef.current.naturalHeight;

    return {
      x: point.x * scaleX,
      y: point.y * scaleY,
    };
  }, []);

  return (
    <div className="image-editor-container">
      <div className="image-editor-controls">
        <h3>Rediger bildet</h3>
        <p>
          Klikk for å legge til punkter og lage rette linjer. Klikk nær det
          første punktet for å lukke formen og fylle den med svart. Dobbeltklikk
          for å fullføre en åpen linje.
        </p>
        <div style={{ marginBottom: "10px" }}>
          <button
            type="button"
            onClick={undoLastDrawing}
            disabled={drawings.length === 0 && !currentDrawing}
            className="image-editor-button image-editor-button--undo"
          >
            {currentDrawing ? "Fjern siste punkt" : "Angre siste"}
          </button>
          <button
            type="button"
            onClick={clearDrawings}
            disabled={drawings.length === 0 && !currentDrawing}
            className="image-editor-button image-editor-button--clear"
          >
            Fjern alle
          </button>
          {currentDrawing && (
            <button
              type="button"
              onClick={cancelCurrentDrawing}
              className="image-editor-button image-editor-button--undo"
              style={{ marginLeft: "10px" }}
            >
              Avbryt tegning
            </button>
          )}
        </div>
        {isDrawing && (
          <div
            style={{
              padding: "8px 12px",
              backgroundColor: "#e3f2fd",
              borderRadius: "4px",
              fontSize: "14px",
              marginBottom: "10px",
            }}
          >
            <strong>Tegner:</strong> {currentDrawing?.points.length || 0}{" "}
            punkter.
            {currentDrawing?.points.length >= 3 &&
              " Klikk nær det første punktet for å lukke formen."}
          </div>
        )}
      </div>

      <div style={{ maxWidth: "100%", overflow: "auto", position: "relative" }}>
        <div className="image-editor-canvas">
          <img
            ref={imgRef}
            alt={imageSrc?.navn || "Edit me"}
            src={imageSrc?.src}
            className="image-editor-image"
            draggable={false}
          />

          {/* Overlay for drawing */}
          <div
            ref={overlayRef}
            className="image-editor-overlay"
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: "crosshair" }}
          >
            {/* SVG overlay for showing drawings */}
            <svg className="image-editor-svg">
              {/* Completed drawings */}
              {drawings.map((drawing, index) => (
                <g key={index}>
                  {drawing.closed ? (
                    // Filled polygon for closed shapes
                    <polygon
                      points={drawing.points
                        .map((point) => getDisplayCoordinates(point))
                        .map((point) => `${point.x},${point.y}`)
                        .join(" ")}
                      fill="black"
                      stroke="black"
                      strokeWidth="2"
                    />
                  ) : (
                    // Line for open shapes
                    <polyline
                      points={drawing.points
                        .map((point) => getDisplayCoordinates(point))
                        .map((point) => `${point.x},${point.y}`)
                        .join(" ")}
                      stroke="black"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  )}
                </g>
              ))}

              {/* Current drawing in progress */}
              {currentDrawing && currentDrawing.points.length > 0 && (
                <g>
                  {/* Draw lines between points */}
                  {currentDrawing.points.length > 1 && (
                    <polyline
                      points={currentDrawing.points
                        .map((point) => getDisplayCoordinates(point))
                        .map((point) => `${point.x},${point.y}`)
                        .join(" ")}
                      stroke="black"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      strokeDasharray="5,5"
                    />
                  )}

                  {/* Draw points as circles */}
                  {currentDrawing.points.map((point, pointIndex) => {
                    const displayPoint = getDisplayCoordinates(point);
                    return (
                      <circle
                        key={pointIndex}
                        cx={displayPoint.x}
                        cy={displayPoint.y}
                        r={pointIndex === 0 ? "8" : "6"}
                        fill={pointIndex === 0 ? "red" : "black"}
                        stroke="white"
                        strokeWidth="2"
                      />
                    );
                  })}

                  {/* Show closing hint line when near first point and have at least 3 points */}
                  {currentDrawing.points.length >= 3 && (
                    <line
                      x1={
                        getDisplayCoordinates(
                          currentDrawing.points[
                            currentDrawing.points.length - 1
                          ],
                        ).x
                      }
                      y1={
                        getDisplayCoordinates(
                          currentDrawing.points[
                            currentDrawing.points.length - 1
                          ],
                        ).y
                      }
                      x2={getDisplayCoordinates(currentDrawing.points[0]).x}
                      y2={getDisplayCoordinates(currentDrawing.points[0]).y}
                      stroke="red"
                      strokeWidth="2"
                      strokeDasharray="3,3"
                      opacity="0.5"
                    />
                  )}
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

ImageEditor.propTypes = {
  setImageEditorRef: PropTypes.func,
  imageSrc: PropTypes.object,
};

export default ImageEditor;
